import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Paper } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
type StepStatus = 'current' | ConceptStatus;
import {
  ConceptStatus,
  getKnowledgeConceptGraph,
  getModuleMasteryApi,
  IModuleMasteryData,
} from '../../api/module';
import { IKnowledgeConcept } from '../../KnowledgeConcepts';
import { buildOrderedConceptNodes } from '../../KnowledgeConcepts/knowledgeConcepts.graph';

export const resolvedStatusLabels = (status?: string) => {
  switch (status) {
    case 'current':
      return 'PAL_LP_STATUS_IN_PROGRESS';
    case 'mastered':
      return 'PAL_LP_STATUS_MASTERED';
    case 'struggling':
      return 'PAL_LP_STATUS_STRUGGLING';
    case 'beginning':
      return 'PAL_LP_STATUS_BEGINNING';
    case 'developing':
      return 'PAL_LP_STATUS_DEVELOPING';
    case 'confident':
      return 'PAL_LP_STATUS_CONFIDENT';
    case 'not_assessed':
    default:
      return 'PAL_LP_STATUS_NOT_ASSESSED';
  }
};

interface StepItem {
  title: string;
  status?: StepStatus;
  subtitle?: string;
}

// const DEFAULT_STEPS: StepItem[] = [
//   { title: 'Basic Kinematics', status: 'completed' },
//   { title: "Newton's Laws", status: 'completed' },
//   { title: 'Motion & Friction', status: 'current' },
//   { title: 'Forces & Drag', status: 'locked' },
//   { title: 'Work, Energy & Power', status: 'locked' },
//   { title: 'Momentum & Collisions', status: 'locked' },
//   { title: 'Circular Motion', status: 'locked' },
//   { title: 'Gravitation Basics', status: 'locked' },
//   { title: 'Fluid Dynamics', status: 'locked' },
// ];

interface LearningPathProps {
  conceptId: string;
  moduleId: string;
  title?: string;
  icon?: string;
  steps?: StepItem[];
  width?: number | string;
  maxStepsHeight?: number | string;
  statusLabels?: Partial<Record<StepStatus, string>>;
}

// const DEFAULT_STATUS_LABELS: Record<StepStatus, string> = {
//   completed: 'Mastered',
//   current: 'In Progress',
//   locked: 'Locked',
// };

const popIn = keyframes`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
`;

const growY = keyframes`
  from {
    transform: scaleY(0);
    opacity: 0;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
`;

const ROW_GAP_PX = 12;
const MAX_VISIBLE = 5;

function getVisibleWindow(steps: StepItem[], t: TFunction): StepItem[] {
  if (steps.length <= MAX_VISIBLE) return steps;

  const currentIdx = steps.findIndex((s) => s.status === 'current');
  const idx = currentIdx === -1 ? 0 : currentIdx;

  let start = idx - 2;
  let end = idx + 3;

  if (start < 0) {
    start = 0;
    end = MAX_VISIBLE;
  }
  if (end > steps.length) {
    end = steps.length;
    start = end - MAX_VISIBLE;
  }

  const visible = steps.slice(start, end);
  const hiddenAbove = start;
  const hiddenBelow = steps.length - end;

  if (hiddenAbove > 0) {
    visible.unshift({
      title: t('PAL_LP_MORE', { count: hiddenAbove }),
      subtitle: 'hidden',
    });
  }
  if (hiddenBelow > 0) {
    visible.push({
      title: t('PAL_LP_MORE', { count: hiddenBelow }),
      subtitle: 'hidden',
    });
  }

  return visible;
}

const getColors = (status?: StepStatus) => {
  switch (status) {
    case 'mastered':
      return {
        circle: '#22c55e',
        primary: '#111827',
        secondary: '#16a34a',
      };
    case 'current':
      return {
        circle: '#6366f1',
        primary: '#4f46e5',
        secondary: '#6366f1',
      };
    default:
      return {
        circle: '#cbd5e1',
        primary: '#94a3b8',
        secondary: '#94a3b8',
      };
  }
};

const StepCircle: React.FC<{ status?: StepStatus; isHidden?: boolean }> = ({
  status,
  isHidden,
}) => {
  if (isHidden) {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#cbd5e1',
          }}
        />
      </Box>
    );
  }
  const colors = getColors(status);

  if (status === 'mastered') {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          bgcolor: colors.circle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${popIn} 0.3s ease-out`,
        }}
      >
        <Box
          sx={{
            animation: `${popIn} 0.28s ease-out 0.16s both`,
          }}
        >
          <CheckIcon sx={{ color: '#fff', fontSize: 12 }} />
        </Box>
      </Box>
    );
  }

  if (status === 'current') {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          bgcolor: '#6366f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${pulse} 1.2s ease-in-out infinite`,
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: '#ffffff',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: '#cbd5e1',
        animation: `${popIn} 0.24s ease-out`,
      }}
    />
  );
};

export const LearningPath: React.FC<LearningPathProps> = ({
  moduleId,
  conceptId,
  title = 'PAL_LP_TITLE',
  icon = '🗺️',
  width = undefined,
  maxStepsHeight = undefined,
  statusLabels,
}) => {
  const { t } = useTranslation();
  const [knowledgeConcepts, setKnowledgeConcepts] = useState<IKnowledgeConcept>(
    {}
  );
  const [moduleMastery, setModuleMastery] = useState<
    IModuleMasteryData | undefined
  >(undefined);
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading'
  );

  const orderedNodes = buildOrderedConceptNodes(
    !!knowledgeConcepts.nodes?.length,
    knowledgeConcepts,
    undefined,
    moduleId
  );

  const masteryMap = new Map(
    moduleMastery?.concept_states?.map((s) => [
      s.concept_id,
      s.concept_status,
    ]) ?? []
  );

  const steps: StepItem[] = orderedNodes.map((node) => {
    const status = masteryMap.get(node.id) as ConceptStatus | undefined;
    return {
      title: node.title || node.name,
      status: node.id === conceptId ? 'current' : status || 'not_assessed',
    };
  });

  const visibleSteps = getVisibleWindow(steps, t);

  useEffect(() => {
    fetchGraphData();
  }, [moduleId, conceptId]);

  const fetchGraphData = async () => {
    try {
      setLoading('loading');
      setModuleMastery(undefined);
      setKnowledgeConcepts({});
      const resGraph = await getKnowledgeConceptGraph({
        module_id: moduleId,
      });
      setKnowledgeConcepts(resGraph.data);
      const moduleMasteryRes = await getModuleMasteryApi({
        module_id: moduleId,
      });
      setModuleMastery(moduleMasteryRes.data);
      setLoading('completed');
    } catch (err) {
      setLoading('error');
    }
  };

  if (loading !== 'completed') {
    return null;
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 3,
        width,
        bgcolor: 'white',
        p: '16px',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        transition: 'all 0.3s',
        mb: '10px',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          p: '16px',
          boxSizing: 'border-box',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-start"
          mb={3}
          sx={{ width: '100%' }}
        >
          <Typography>{icon}</Typography>
          <Typography fontWeight={600}>{t(title)}</Typography>
        </Stack>

        <Stack
          spacing={0}
          sx={{
            width: '100%',
            pt: 1,
            maxHeight: maxStepsHeight,
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#cbd5e1',
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {visibleSteps.map((step, index) => {
            const colors = getColors(step.status);
            const isLast = index === visibleSteps.length - 1;

            return (
              <Stack
                key={index}
                direction="row"
                spacing={2}
                sx={{
                  mb: isLast ? 0 : `${ROW_GAP_PX}px`,
                  minWidth: 0,
                }}
              >
                {/* Timeline */}
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <StepCircle status={step.status || 'not_assessed'} />
                  </Box>

                  {!isLast && (
                    <Box
                      sx={{
                        width: 2,
                        position: 'absolute',
                        top: 18,
                        bottom: `-${ROW_GAP_PX}px`,
                        bgcolor: colors.circle,
                        transformOrigin: 'top center',
                        animation: `${growY} 0.4s ease ${
                          0.12 + index * 0.03
                        }s both`,
                      }}
                    />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    color={colors.primary}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {step.title}
                  </Typography>

                  {step.status && (
                    <Typography
                      fontSize={13}
                      color={colors.secondary}
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {t(resolvedStatusLabels(step.status))}
                    </Typography>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};
